import axios from "axios";
import { getCached, prefetchCached } from "./queryCache";
import { clearAuthSession, getAuthItem } from "../utils/auth";
import { API_BASE_URL } from "../config/apiConfig";

let activeMutatingRequestCount = 0;
let authRedirectInProgress = false;

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

const PUBLIC_AUTH_ENDPOINTS = new Set([
  "/auth/login",
  "/auth/verify-2fa",
  "/auth/inventory-email-access",
  "/auth/forgot-password",
  "/auth/verify-otp",
  "/auth/reset-password",
]);

const isMutatingMethod = (method = "") =>
  MUTATING_METHODS.has(String(method || "").trim().toLowerCase());

const normalizeRequestPath = (config = {}) => {
  const rawUrl = String(config?.url || "").trim();

  if (!rawUrl) {
    return "";
  }

  const withoutHash = rawUrl.split("#")[0];
  const withoutQuery = withoutHash.split("?")[0];
  const withoutOrigin = withoutQuery.replace(/^https?:\/\/[^/]+/i, "");
  const withLeadingSlash = withoutOrigin.startsWith("/")
    ? withoutOrigin
    : `/${withoutOrigin}`;

  if (withLeadingSlash.length > 1) {
    return withLeadingSlash.replace(/\/+$/, "");
  }

  return withLeadingSlash;
};

const isPublicAuthRequest = (config = {}) => {
  const requestPath = normalizeRequestPath(config);

  if (!requestPath) {
    return false;
  }

  if (PUBLIC_AUTH_ENDPOINTS.has(requestPath)) {
    return true;
  }

  return [...PUBLIC_AUTH_ENDPOINTS].some((endpoint) =>
    requestPath.endsWith(endpoint)
  );
};

const incrementMutatingCount = (config = {}) => {
  if (
    isMutatingMethod(config.method) &&
    !config.__isTrackedMutatingRequest
  ) {
    activeMutatingRequestCount += 1;
    config.__isTrackedMutatingRequest = true;
  }

  return config;
};

const decrementMutatingCount = (config = {}) => {
  if (config?.__isTrackedMutatingRequest) {
    activeMutatingRequestCount = Math.max(
      0,
      activeMutatingRequestCount - 1
    );

    config.__isTrackedMutatingRequest = false;
  }
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: "application/json",
  },
});

/* =========================================================
   REQUEST INTERCEPTOR
========================================================= */

api.interceptors.request.use(
  (config) => {
    incrementMutatingCount(config);

    const token = getAuthItem("token");

    const skipAuthHeader =
      config?.skipAuthHeader === true ||
      isPublicAuthRequest(config);

    if (token && !skipAuthHeader) {
      if (!config.headers) {
        config.headers = {};
      }

      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    decrementMutatingCount(error?.config);
    return Promise.reject(error);
  }
);

/* =========================================================
   RESPONSE INTERCEPTOR
========================================================= */

api.interceptors.response.use(
  (response) => {
    decrementMutatingCount(response?.config);
    return response;
  },
  (error) => {
    const config = error?.config || {};

    decrementMutatingCount(config);

    const status = Number(error?.response?.status || 0);

    const skipAuthRedirect =
      config?.skipAuthRedirect === true;

    const publicAuthRequest =
      isPublicAuthRequest(config);

    const shouldRedirectToLogin =
      status === 401 &&
      !skipAuthRedirect &&
      !publicAuthRequest;

    if (shouldRedirectToLogin) {
      clearAuthSession();

      if (typeof window !== "undefined") {
        const currentPath = window.location?.pathname || "";

        if (
          currentPath !== "/login" &&
          !authRedirectInProgress
        ) {
          authRedirectInProgress = true;
          window.location.replace("/login");
        }
      }
    }

    return Promise.reject(error);
  }
);

/* =========================================================
   CACHED GET
========================================================= */

export const cachedGet = (url, options = {}) =>
  getCached(
    (targetUrl, requestOptions) =>
      api.get(targetUrl, requestOptions),
    url,
    options
  );

/* =========================================================
   PREFETCH GET
========================================================= */

export const prefetchGet = (url, options = {}) =>
  prefetchCached(
    (targetUrl, requestOptions) =>
      api.get(targetUrl, requestOptions),
    url,
    options
  );

/* =========================================================
   MUTATING REQUEST COUNT
========================================================= */

export const getActiveMutatingRequestCount = () =>
  activeMutatingRequestCount;

export default api;
